import type { ColourRoleName, PaletteName } from "../ui/tokens.ts";
import { MINIMAP_FIELD, MINIMAP_PIN_STANDS_FOR_TILES, PARTY_COLOURS, PARTY_PLACES, inMinimapField, inMinimapVision, partyColourOf } from "./minimap.ts";

/**
 * The compass round the HUD minimap (ADR-061's amendment of 2026-10-14 and the author's answers to it): pure geometry,
 * the one rule for a mark or a pip, which targets get a pip, how pips are spread when they crowd and how each kind
 * looks. No DOM and no canvas; the client paints what this module decides.
 */

/**
 * The compass's geometry, each a number of logical pixels.
 */
export interface CompassGeometry {
  readonly slot: number;
  readonly band: number;
  readonly field: number;
  readonly player: number;
  readonly lineMin: number;
  readonly lineMax: number;
}

/**
 * The slot's geometry in logical pixels, origin at its top left: the slot 90, the band 7 wide on every side inside the
 * frame's 3, the field 70, the player's own pixel 45 and the band's middle line, the row and column of every pip's
 * centre, from 6 to 83 (39 pixels from the player's pixel up and left, 38 down and right).
 */
export const COMPASS: CompassGeometry = Object.freeze({ slot: 90, band: 7, field: 70, player: 45, lineMin: 6, lineMax: 83 });

/**
 * The places round the middle line: four sides of 77 steps.
 */
export const COMPASS_RING = 4 * (COMPASS.lineMax - COMPASS.lineMin);

/**
 * The most pips the compass holds at once, the size of the client's pool of buttons.
 */
export const COMPASS_MAX_PIPS = 16;

/**
 * The known places on the compass when the setting is not chosen (the author, 2026-10-14: nearest 3).
 */
export const COMPASS_PLACES_DEFAULT = 3;

/**
 * The most known places the setting allows (the author, 2026-10-14: up to 12).
 */
export const COMPASS_PLACES_MOST = 12;

/**
 * What a pip can stand for, from the highest of the draw order to the lowest.
 */
export const COMPASS_KINDS = Object.freeze(["pin", "quest", "party", "place"] as const);

/**
 * A kind of pip.
 */
export type CompassKind = (typeof COMPASS_KINDS)[number];

/**
 * A pip's shape: the 5 by 5 core the client draws.
 */
export type CompassShape = "teardrop" | "star" | "square" | "diamond";

/**
 * How a kind looks: its shape, the core's size, the colour tokens (one, or for a party member the six of the roster
 * places, indexed by place), the outline token and the draw order, 1 for the one drawn on top.
 */
export interface CompassLook {
  readonly shape: CompassShape;
  readonly size: number;
  readonly tokens: readonly (PaletteName | ColourRoleName)[];
  readonly outline: PaletteName | ColourRoleName;
  readonly order: number;
}

/**
 * The look of each kind (ADR-061's table of item 7, with the party's six colours of the author's answers): the pin a
 * brass teardrop, the quest target a yellow star, a party member a square in the colour of their roster place and a
 * known place a white diamond, each a 5 by 5 core with a 1 px `ink-900` outline.
 */
export const COMPASS_LOOKS: Readonly<Record<CompassKind, CompassLook>> = Object.freeze({
  pin: Object.freeze({ shape: "teardrop", size: 5, tokens: Object.freeze(["brass-100"] as const), outline: "ink-900", order: 1 }),
  quest: Object.freeze({ shape: "star", size: 5, tokens: Object.freeze(["warning"] as const), outline: "ink-900", order: 2 }),
  party: Object.freeze({ shape: "square", size: 5, tokens: PARTY_COLOURS, outline: "ink-900", order: 3 }),
  place: Object.freeze({ shape: "diamond", size: 5, tokens: Object.freeze(["text"] as const), outline: "ink-900", order: 4 }),
});

/**
 * A place in the slot's logical pixels.
 */
export interface CompassPoint {
  readonly x: number;
  readonly y: number;
}

/**
 * Where the ray from the player toward a target meets the band's middle line: the offset's direction is scaled until
 * it reaches the square of the line and the point is rounded to a whole pixel. The place does not depend on how far
 * the target is, nor on the zoom.
 *
 * @param dx Tiles across from the player to the target.
 * @param dy Tiles down from the player to the target.
 * @returns The place in the slot's pixels, or null when the offset is zero and there is no ray.
 */
export function compassPoint(dx: number, dy: number): CompassPoint | null {
  if (dx === 0 && dy === 0) {
    return null;
  }
  const reachX = dx < 0 ? COMPASS.player - COMPASS.lineMin : COMPASS.lineMax - COMPASS.player;
  const reachY = dy < 0 ? COMPASS.player - COMPASS.lineMin : COMPASS.lineMax - COMPASS.player;
  const along = Math.min(reachX / Math.abs(dx), reachY / Math.abs(dy));
  return { x: Math.round(COMPASS.player + dx * along), y: Math.round(COMPASS.player + dy * along) };
}

/**
 * Whether the field draws a mark for a target, so that it has no pip: a party member has one inside vision and where
 * the rounded centre is inside the field (P1.9-T179 and T180's rule); the pin, a quest target and a known place have
 * one wherever the centre is inside the field, vision not applying, since the player knows their own.
 *
 * @param kind What the target is.
 * @param dx Tiles across from the player to the target.
 * @param dy Tiles down from the player to the target.
 * @param scale The field's logical pixels a tile.
 * @returns True when the field draws it.
 */
export function hasMark(kind: CompassKind, dx: number, dy: number, scale: number): boolean {
  if (kind === "party" && !inMinimapVision(dx, dy)) {
    return false;
  }
  return inMinimapField(dx, dy, MINIMAP_FIELD, scale);
}

/**
 * How near a known place may be to the pin, in tiles, before the pin stands for it and the place has no pip.
 */
export const COMPASS_PIN_STANDS_FOR_TILES = MINIMAP_PIN_STANDS_FOR_TILES;

/**
 * Someone the page holds a position for. Only a member of the party, with their roster place, ever has a pip; any
 * other player and any creature is given here so that the rule is held in one place and is never a pip.
 */
export type CompassOther =
  | { readonly kind: "party"; readonly handle: number; readonly name: string; readonly place: number; readonly mapId: number; readonly x: number; readonly y: number }
  | { readonly kind: "player" | "creature"; readonly handle: number; readonly name: string; readonly mapId: number; readonly x: number; readonly y: number };

/**
 * A place the page holds for the Map, and whether the character knows it. Only a known place ever has a pip.
 */
export interface CompassPlaceInput {
  readonly handle: number;
  readonly name: string;
  readonly x: number;
  readonly y: number;
  readonly discovered: boolean;
}

/**
 * Everything `chooseCompassPips` reads, positions in tiles: the player, the map they are on, the field's scale, who
 * else the page knows, the pin, the tracked quest's target on this map, the known places and how many of them the
 * setting allows (0 to `COMPASS_PLACES_MOST`).
 */
export interface CompassInputs {
  readonly self: { readonly x: number; readonly y: number };
  readonly mapId: number;
  readonly scale: number;
  readonly others: readonly CompassOther[];
  readonly pin: { readonly x: number; readonly y: number } | null;
  readonly quest: { readonly title: string; readonly x: number; readonly y: number } | null;
  readonly places: readonly CompassPlaceInput[];
  readonly placeCount: number;
}

/**
 * One pip: its kind, the handle and name it stands for (the quest's title, and 0 and empty where there is none), the
 * roster place that gives a party member's colour (0 for the rest), the offset in tiles from the player and its place
 * on the middle line in the slot's pixels.
 */
export interface CompassPip {
  readonly kind: CompassKind;
  readonly handle: number;
  readonly name: string;
  readonly place: number;
  readonly dx: number;
  readonly dy: number;
  readonly x: number;
  readonly y: number;
}

/**
 * A pip while it is being chosen.
 */
interface BuildingPip {
  kind: CompassKind;
  handle: number;
  name: string;
  place: number;
  dx: number;
  dy: number;
  x: number;
  y: number;
}

/**
 * Sets a pip's fields from a target's offset.
 *
 * @param pip The pip to write.
 * @param kind The kind.
 * @param handle The handle, 0 for none.
 * @param name The name.
 * @param place The roster place, 0 for none.
 * @param dx Tiles across.
 * @param dy Tiles down.
 * @param at The place on the line.
 * @returns The pip.
 */
function setPip(pip: BuildingPip, kind: CompassKind, handle: number, name: string, place: number, dx: number, dy: number, at: CompassPoint): BuildingPip {
  pip.kind = kind;
  pip.handle = handle;
  pip.name = name;
  pip.place = place;
  pip.dx = dx;
  pip.dy = dy;
  pip.x = at.x;
  pip.y = at.y;
  return pip;
}

const PIP_POOL: BuildingPip[] = Array.from({ length: COMPASS_MAX_PIPS }, () => ({ kind: COMPASS_KINDS[0], handle: 0, name: "", place: 0, dx: 0, dy: 0, x: 0, y: 0 }));

/**
 * Chooses the pips: the pin, the tracked quest's target, up to five members of the party on this map, and the nearest
 * N known places, each only when the field draws no mark for it (`hasMark`), a place within
 * `COMPASS_PIN_STANDS_FOR_TILES` of the pin dropped, and nothing else: another map's member, a player outside the
 * party, a creature and an undiscovered place never have a pip. At most `COMPASS_MAX_PIPS`, the farthest places the
 * first to go.
 *
 * @param input What the page holds.
 * @returns The pips in draw order, the top first: the pin, the quest, the party in the order given, then the places
 * nearest first. The pips are objects of a pool reused by the next call, so a caller that keeps one past its next
 * call copies it.
 * @throws RangeError when the place count is not a whole number from 0 to `COMPASS_PLACES_MOST` or a party member's
 * roster place is not 0 to 5.
 */
export function chooseCompassPips(input: CompassInputs): CompassPip[] {
  const { self, scale, pin, quest } = input;
  if (!Number.isInteger(input.placeCount) || input.placeCount < 0 || input.placeCount > COMPASS_PLACES_MOST) {
    throw new RangeError(`compass places ${input.placeCount} is outside 0 to ${COMPASS_PLACES_MOST}`);
  }
  const out: BuildingPip[] = [];
  let claimed = 0;
  const claim = (): BuildingPip => PIP_POOL[claimed++] ?? ({} as BuildingPip);
  if (pin !== null) {
    const dx = pin.x - self.x;
    const dy = pin.y - self.y;
    const at = hasMark("pin", dx, dy, scale) ? null : compassPoint(dx, dy);
    if (at !== null) {
      out.push(setPip(claim(), "pin", 0, "", 0, dx, dy, at));
    }
  }
  if (quest !== null) {
    const dx = quest.x - self.x;
    const dy = quest.y - self.y;
    const at = hasMark("quest", dx, dy, scale) ? null : compassPoint(dx, dy);
    if (at !== null) {
      out.push(setPip(claim(), "quest", 0, quest.title, 0, dx, dy, at));
    }
  }
  let members = 0;
  for (const other of input.others) {
    if (other.kind !== "party") {
      continue;
    }
    partyColourOf(other.place);
    if (other.mapId !== input.mapId || members >= PARTY_PLACES - 1) {
      continue;
    }
    members += 1;
    const dx = other.x - self.x;
    const dy = other.y - self.y;
    const at = hasMark("party", dx, dy, scale) ? null : compassPoint(dx, dy);
    if (at !== null) {
      out.push(setPip(claim(), "party", other.handle, other.name, other.place, dx, dy, at));
    }
  }
  const first = out.length;
  const room = Math.min(input.placeCount, COMPASS_MAX_PIPS - first);
  if (room > 0) {
    for (const place of input.places) {
      if (!place.discovered) {
        continue;
      }
      const dx = place.x - self.x;
      const dy = place.y - self.y;
      if (pin !== null && (place.x - pin.x) ** 2 + (place.y - pin.y) ** 2 <= COMPASS_PIN_STANDS_FOR_TILES ** 2) {
        continue;
      }
      const at = hasMark("place", dx, dy, scale) ? null : compassPoint(dx, dy);
      if (at === null) {
        continue;
      }
      const distance = dx * dx + dy * dy;
      const full = out.length - first >= room;
      const last = out[out.length - 1] as BuildingPip;
      if (full && distance >= last.dx * last.dx + last.dy * last.dy) {
        continue;
      }
      const pip = full ? (out.pop() as BuildingPip) : claim();
      out.push(setPip(pip, "place", place.handle, place.name, 0, dx, dy, at));
      for (let slot = out.length - 1; slot > first; slot -= 1) {
        const above = out[slot - 1] as BuildingPip;
        if (above.dx * above.dx + above.dy * above.dy <= distance) {
          break;
        }
        out[slot - 1] = out[slot] as BuildingPip;
        out[slot] = above;
      }
    }
  }
  return out;
}

/**
 * The gap, in pixels along the line, that pips are spread to. Pips under it are one cluster and are spread; pips at it
 * stay joined to their cluster, so a spread cluster is still one cluster and spreading again changes nothing.
 */
export const COMPASS_CLUSTER_GAP = 6;

/**
 * The place round the middle line of a point on it: 0 at the top left corner, then clockwise along the top, down the
 * right, back along the bottom and up the left, 0 to `COMPASS_RING` less one.
 *
 * @param point A point on the middle line.
 * @returns The place.
 * @throws RangeError when the point is not on the line.
 */
export function compassRingPlace(point: CompassPoint): number {
  const { lineMin, lineMax } = COMPASS;
  const side = lineMax - lineMin;
  const { x, y } = point;
  if (x < lineMin || x > lineMax || y < lineMin || y > lineMax) {
    throw new RangeError(`compass point ${x},${y} is off the line`);
  }
  if (y === lineMin) {
    return x - lineMin;
  }
  if (x === lineMax) {
    return side + (y - lineMin);
  }
  if (y === lineMax) {
    return 2 * side + (lineMax - x);
  }
  if (x === lineMin) {
    return 3 * side + (lineMax - y);
  }
  throw new RangeError(`compass point ${x},${y} is off the line`);
}

/**
 * The point on the middle line at a place round it.
 *
 * @param place A whole number of pixels round the line; any whole number wraps into the ring.
 * @returns The point.
 */
export function compassRingPoint(place: number): CompassPoint {
  const { lineMin, lineMax } = COMPASS;
  const side = lineMax - lineMin;
  const at = ((place % COMPASS_RING) + COMPASS_RING) % COMPASS_RING;
  if (at < side) {
    return { x: lineMin + at, y: lineMin };
  }
  if (at < 2 * side) {
    return { x: lineMax, y: lineMin + (at - side) };
  }
  if (at < 3 * side) {
    return { x: lineMax - (at - 2 * side), y: lineMax };
  }
  return { x: lineMin, y: lineMax - (at - 3 * side) };
}

const stackAlong = new Float64Array(COMPASS_MAX_PIPS);
const stackOrder = new Int32Array(COMPASS_MAX_PIPS);

/**
 * A sorted pip's place, counted on from the cut so that the ring reads as a straight run.
 *
 * @param cut Where in the order the run starts.
 * @param index The position in the run, 0 up.
 * @param origin The place at the cut.
 * @param count How many pips there are.
 * @returns The place, `COMPASS_RING` added when the run has crossed the seam.
 */
function unwrapAlong(cut: number, index: number, origin: number, count: number): number {
  const along = stackAlong[stackOrder[(cut + index) % count] as number] as number;
  return along < origin ? along + COMPASS_RING : along;
}

/**
 * Spreads pips that crowd: pips whose centres lie less than `COMPASS_CLUSTER_GAP` apart along the line form a
 * cluster (a chain of pips exactly that far apart stays one, and is already spread), and a cluster is centred on the mean of its members and set that far apart in their order round the frame,
 * turning corners and crossing the top left seam as the line does. It repeats until nothing is within the gap, so a
 * second pass changes nothing. A pip so moved is off its bearing by half a gap for each other member of its cluster at
 * most.
 *
 * @param pips The pips, each on the line, at most `COMPASS_MAX_PIPS`.
 * @returns Copies of the pips in the order given, with the places moved.
 * @throws RangeError when there are more than `COMPASS_MAX_PIPS` or a pip is off the line.
 */
export function stackCompassPips(pips: readonly CompassPip[]): CompassPip[] {
  const count = pips.length;
  if (count > COMPASS_MAX_PIPS) {
    throw new RangeError(`${count} compass pips is more than ${COMPASS_MAX_PIPS}`);
  }
  for (let at = 0; at < count; at += 1) {
    stackAlong[at] = compassRingPlace(pips[at] as CompassPip);
  }
  for (let pass = 0; pass < 2 * COMPASS_MAX_PIPS && count > 1; pass += 1) {
    for (let at = 0; at < count; at += 1) {
      let slot = at;
      while (slot > 0 && (stackAlong[stackOrder[slot - 1] as number] as number) > (stackAlong[at] as number)) {
        stackOrder[slot] = stackOrder[slot - 1] as number;
        slot -= 1;
      }
      stackOrder[slot] = at;
    }
    let cut = -1;
    for (let at = 0; at < count && cut < 0; at += 1) {
      const before = stackAlong[stackOrder[(at + count - 1) % count] as number] as number;
      const here = stackAlong[stackOrder[at] as number] as number;
      if ((at === 0 ? here - before + COMPASS_RING : here - before) > COMPASS_CLUSTER_GAP) {
        cut = at;
      }
    }
    if (cut < 0) {
      break;
    }
    const origin = stackAlong[stackOrder[cut] as number] as number;
    let moved = false;
    let from = 0;
    while (from < count) {
      let to = from + 1;
      let sum = unwrapAlong(cut, from, origin, count);
      while (to < count && unwrapAlong(cut, to, origin, count) - unwrapAlong(cut, to - 1, origin, count) <= COMPASS_CLUSTER_GAP) {
        sum += unwrapAlong(cut, to, origin, count);
        to += 1;
      }
      const size = to - from;
      if (size > 1) {
        const start = Math.round(sum / size - (COMPASS_CLUSTER_GAP * (size - 1)) / 2);
        for (let member = 0; member < size; member += 1) {
          const index = stackOrder[(cut + from + member) % count] as number;
          const place = (((start + COMPASS_CLUSTER_GAP * member) % COMPASS_RING) + COMPASS_RING) % COMPASS_RING;
          if (stackAlong[index] !== place) {
            moved = true;
          }
          stackAlong[index] = place;
        }
      }
      from = to;
    }
    if (!moved) {
      break;
    }
  }
  const out: CompassPip[] = new Array<CompassPip>(count);
  for (let at = 0; at < count; at += 1) {
    const pip = pips[at] as CompassPip;
    const place = stackAlong[at] as number;
    const point = place === compassRingPlace(pip) ? pip : compassRingPoint(place);
    out[at] = { kind: pip.kind, handle: pip.handle, name: pip.name, place: pip.place, dx: pip.dx, dy: pip.dy, x: point.x, y: point.y };
  }
  return out;
}
